import type {
  ActivityRowIconTone,
  CompanyAvatarTextColor,
  CompanyAvatarTone,
  RevealMotion,
  RevealVariant,
} from "./types";

export const PARTNER_ONBOARDING_STEP_IDS = [
  "intro",
  "commission",
  "paywall",
  "payout",
] as const;

export const PARTNER_PROGRAM_PATH = "/account/partner_program";

// Motion curves from the DS prototype (refer-circle).
export const EASE_OUT_CLASS = "ease-[cubic-bezier(0.23,1,0.32,1)]";
export const EASE_IN_OUT_CLASS = "ease-[cubic-bezier(0.77,0,0.175,1)]";

const EASE_OUT: [number, number, number, number] = [0.23, 1, 0.32, 1];
const EASE_POP: [number, number, number, number] = [0.34, 1.45, 0.64, 1];

export const PAYOUT_PHASES = [
  "idle",
  "earnings",
  "ready",
  "pressing",
  "paying",
  "paid",
] as const;

export const EARNINGS_TARGET_CENTS = 132400;
export const EARNINGS_COUNT_UP_MS = 1400;
export const EARNINGS_COUNT_UP_DELAY_MS = 300;

// Keep the duration class in sync with PAYOUT_PROGRESS_MS.
export const PAYOUT_PROGRESS_MS = 1700;
// Fills over the progress time, then fades out in 200ms once paid.
export const PAYOUT_PROGRESS_DURATION_CLASS =
  "[transition-duration:1700ms,200ms]";

export const CHIP_DRIFT_PX = 8;

export const INTRO_RINGS = [
  {
    className: "size-[136.075px]",
    circleClassName: "border border-primary/20 bg-primary/10 shadow-3xl",
  },
  {
    className: "size-[240.53px] opacity-60",
    circleClassName: "border border-primary/20 bg-primary/20 shadow-3xl",
  },
  {
    className: "size-[347.81px] opacity-30",
    circleClassName: "border border-primary/20 bg-primary/20 shadow-3xl",
  },
  {
    className: "size-[466px] opacity-30",
    circleClassName: "bg-primary/5 shadow-3xl",
  },
] as const;

export const FEED_DOT_POSITION_CLASSES = [
  "left-1/2 top-[116px] -ml-[50px]",
  "left-1/2 top-[71px] -ml-[89px]",
  "left-1/2 top-[71px] -ml-[59px]",
  "left-1/2 top-[36px] -ml-[80px]",
] as const;

export const COMPANY_AVATAR_CLASSES: Record<CompanyAvatarTone, string> = {
  green: "bg-success-light",
  pink: "bg-danger-light",
  purple: "bg-level-top",
  yellow: "bg-level-basic",
};

export const COMPANY_AVATAR_TEXT_COLORS: Record<
  CompanyAvatarTone,
  CompanyAvatarTextColor
> = {
  green: "success",
  pink: "danger",
  purple: "level-top",
  yellow: "level-basic",
};

export const ACTIVITY_ICON_CLASSES: Record<ActivityRowIconTone, string> = {
  green: "bg-success",
  salmon: "bg-salmon-400",
};

export const REVEAL_MOTION: Record<RevealVariant, RevealMotion> = {
  drop: {
    hidden: { opacity: 0, y: -8, scale: 0.97, filter: "blur(3px)" },
    visible: { opacity: 1, y: 0, scale: 1, filter: "blur(0px)" },
    transition: {
      duration: 0.6,
      ease: EASE_OUT,
      opacity: { duration: 0.4, ease: EASE_OUT },
    },
  },
  focus: {
    hidden: { opacity: 0, scale: 0.96, filter: "blur(4px)" },
    visible: { opacity: 1, scale: 1, filter: "blur(0px)" },
    transition: { duration: 1.2, ease: EASE_OUT },
  },
  grow: {
    hidden: { opacity: 0, scale: 0.3 },
    visible: { opacity: 1, scale: 1 },
    transition: { duration: 0.9, ease: EASE_OUT },
  },
  lift: {
    hidden: { opacity: 0, y: 12 },
    visible: { opacity: 1, y: 0 },
    transition: { duration: 0.45, ease: EASE_OUT },
  },
  pop: {
    hidden: { opacity: 0, scale: 0.6, filter: "blur(4px)" },
    visible: { opacity: 1, scale: 1, filter: "blur(0px)" },
    transition: { duration: 0.5, ease: EASE_POP },
  },
  rise: {
    hidden: { opacity: 0, y: 16 },
    visible: { opacity: 1, y: 0 },
    transition: { duration: 0.45, ease: EASE_OUT },
  },
};
